import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, InputNumber, Switch, message, Space, Tag, Popconfirm, Alert, Card, Grid, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined, EyeOutlined } from '@ant-design/icons';
import cdnService from '../../services/cdnService';

import { userPath } from '../../../shared/config/paths';

const { useBreakpoint } = Grid;

export default function DomainWorker() {
  const { t } = useTranslation();
  const { domainName } = useParams();
  const { subscriptionInfo } = useOutletContext();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [domainWorkers, setDomainWorkers] = useState([]);
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBinding, setEditingBinding] = useState(null);
  const [form] = Form.useForm();
  const [workerQuota, setWorkerQuota] = useState(null);

  useEffect(() => {
    fetchDomainWorkers();
    fetchAvailableWorkers();
    loadQuota();
  }, [domainName, subscriptionInfo]);

  const fetchDomainWorkers = async () => {
    setLoading(true);
    try {
      const res = await cdnService.getDomainWorkers(domainName);
      if (res.success) {
        setDomainWorkers(res.data || []);
      }
    } catch (error) {
      message.error(t('Failed to load: {{message}}', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const loadQuota = () => {
    if (subscriptionInfo?.resource_quota_usage?.domains) {
      const domainEntries = Object.values(subscriptionInfo.resource_quota_usage.domains);
      const found = domainEntries.find((d) => d.domain_name === domainName);
      const quota = found?.quotas?.cdn_worker_routes_limit;
      if (quota) {
        setWorkerQuota(quota);
      }
    }
  };

  const fetchAvailableWorkers = async () => {
    try {
      const res = await cdnService.getAvailableWorkers(domainName);
      if (res.success) {
        setAvailableWorkers(res.data || []);
      }
    } catch (error) {
      message.error(t('Failed to load available Workers: {{message}}', { message: error.message }));
    }
  };

  const handleCreate = () => {
    if (workerQuota && !workerQuota.is_unlimited) {
      const limit = workerQuota.limit ?? workerQuota.limit_value ?? -1;
      const used = workerQuota.used ?? workerQuota.current ?? domainWorkers.length;
      if (limit > -1 && used >= limit) {
        Modal.warning({
          title: t('Worker route limit reached'),
          content: t('Your current plan supports up to {{limit}} Worker routes. Please upgrade your plan or delete an existing route before adding another.', { limit })
        });
        return;
      }
    }

    setEditingBinding(null);
    form.resetFields();
    form.setFieldsValue({
      route_pattern: '/*',
      priority: 0,
      phase: 'request',
      enabled: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (binding) => {
    setEditingBinding(binding);
    form.setFieldsValue({
      worker_id: binding.worker_id,
      route_pattern: binding.route_pattern,
      priority: binding.priority,
      phase: binding.phase || 'request',
      enabled: binding.enabled,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await cdnService.deleteDomainWorker(domainName, id);
      if (res.success) {
        message.success(t('Deleted successfully'));
        fetchDomainWorkers();
      } else {
        message.error(res.error || t('Deletion failed'));
      }
    } catch (error) {
      message.error(t('Deletion failed: {{message}}', { message: error.message }));
    }
  };

  const handleToggle = async (binding) => {
    try {
      const res = await cdnService.toggleDomainWorker(domainName, binding.id, !binding.enabled);
      if (res.success) {
        message.success(binding.enabled ? t('Disabled') : t('Enabled'));
        fetchDomainWorkers();
      } else {
        message.error(res.error || t('Operation failed'));
      }
    } catch (error) {
      message.error(t('Operation failed: {{message}}', { message: error.message }));
    }
  };

  const handleViewWorker = (workerId) => {
    navigate(userPath(`/cdn/workers/${workerId}`));
  };

  const handleManageWorkers = () => {
    navigate(userPath('/cdn/workers'));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        worker_id: values.worker_id,
        route_pattern: values.route_pattern,
        priority: values.priority ?? 0,
        phase: values.phase || 'request',
        enabled: values.enabled ?? true,
      };

      if (editingBinding) {
        data.id = editingBinding.id;
      }

      const res = await cdnService.saveDomainWorker(domainName, data);

      if (res.success) {
        message.success(editingBinding ? t('Updated successfully') : t('Created successfully'));
        setModalVisible(false);
        fetchDomainWorkers();
      } else {
        message.error(res.error || t('Failed to save'));
      }
    } catch (error) {
      if (error.errorFields) {
        message.error(t('Please check the form input'));
      } else {
        message.error(t('Failed to save: {{message}}', { message: error.message }));
      }
    }
  };

  const columns = [
    {
      title: 'Worker',
      dataIndex: 'worker_name',
      width: 200,
      render: (name, record) => (
        <div>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => handleViewWorker(record.worker_id)}
          >
            {name}
          </Button>
          <div style={{ fontSize: 12, color: '#999' }}>v{record.worker_version}</div>
        </div>
      ),
    },
    {
      title: t('Route pattern'),
      dataIndex: 'route_pattern',
      width: 200,
      render: (pattern) => <code>{pattern}</code>,
    },
    {
      title: t('Priority'),
      dataIndex: 'priority',
      width: 100,
      sorter: (a, b) => b.priority - a.priority,
    },
    {
      title: t('Execution phase'),
      dataIndex: 'phase',
      width: 100,
      render: (phase) => (phase === 'response' ? <Tag color="purple">{t('Response phase')}</Tag> : <Tag>{t('Request phase')}</Tag>),
    },
    {
      title: t('Status'),
      dataIndex: 'enabled',
      width: 100,
      render: (enabled) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? t('Enabled') : t('Disabled')}
        </Tag>
      ),
    },
    {
      title: t('Updated at'),
      dataIndex: 'updated_at',
      width: 180,
    },
    {
      title: t('Actions'),
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >{t('Edit')}</Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggle(record)}
          >
            {record.enabled ? t('Disable') : t('Enable')}
          </Button>
          <Popconfirm
            title={t('Delete this Worker binding?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('OK')}
            cancelText={t('Cancel')}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >{t('Delete')}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Mobile card renderer
  const renderMobileCard = (binding) => (
    <Card
      key={binding.id}
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto', fontWeight: 500 }}
            onClick={() => handleViewWorker(binding.worker_id)}
          >
            {binding.worker_name}
          </Button>
          <Tag color={binding.enabled ? 'green' : 'default'} style={{ marginLeft: 8, flexShrink: 0 }}>
            {binding.enabled ? t('Enable') : t('Disable')}
          </Tag>
        </div>
      }
    >
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        <div style={{ color: '#666', marginBottom: 4 }}>{t('Route pattern:')}</div>
        <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>
          {binding.route_pattern}
        </code>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Tag color="blue">v{binding.worker_version}</Tag>
        <Tag>{t('Priority: {{priority}}', { priority: binding.priority })}</Tag>
        <Tag color={binding.phase === 'response' ? 'purple' : 'default'}>{binding.phase === 'response' ? t('Response phase') : t('Request phase')}</Tag>
      </div>
      <Space wrap size="small">
        <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewWorker(binding.worker_id)}>{t('View')}</Button>
        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(binding)}>{t('Edit')}</Button>
        <Button size="small" onClick={() => handleToggle(binding)}>
          {binding.enabled ? t('Disable') : t('Enable')}
        </Button>
        <Popconfirm
          title={t('Delete this Worker binding?')}
          onConfirm={() => handleDelete(binding.id)}
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Button size="small" danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div className="domain-worker-page" style={{ padding: isMobile ? '12px 0' : 0 }}>
      <Alert
        message={t('Worker route bindings')}
        description={isMobile
          ? t('Configure Worker rules for {{domainName}}', { domainName })
          : t('Configure Worker execution rules for {{domainName}}. The Worker matches route patterns from highest to lowest priority.', { domainName })
        }
        type="info"
        showIcon
        style={{ marginBottom: isMobile ? 12 : 16 }}
      />

      <div style={{
        marginBottom: isMobile ? 12 : 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <h3 style={{ margin: 0, fontSize: isMobile ? 16 : 18 }}>{t('Worker binding list')}</h3>
        <Space size={isMobile ? 'small' : 'middle'} wrap>
          <Button
            icon={<SettingOutlined />}
            onClick={handleManageWorkers}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? t('Manage') : t('Manage Workers')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? t('Add') : t('Add binding')}
          </Button>
        </Space>
      </div>

      {isMobile ? (
        <div>
          {domainWorkers.map(binding => renderMobileCard(binding))}
          {domainWorkers.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>{t('No Worker bindings yet')}</div>
          )}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={domainWorkers}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description={t('No Worker bindings yet. Click "Add binding" in the top right.')} /> }}
        />
      )}

      <Modal
        title={editingBinding ? t('Edit Worker binding') : t('Add Worker binding')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 20 } : undefined}
        okText={t('Save')}
        cancelText={t('Cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t('Select a Worker')}
            name="worker_id"
            rules={[{ required: true, message: t('Select a Worker') }]}
          >
            <Select
              placeholder={t('Select a Worker')}
              showSearch
              optionFilterProp="children"
              disabled={!!editingBinding}
            >
              {availableWorkers.map(worker => (
                <Select.Option key={worker.id} value={worker.id}>
                  {worker.name} (v{worker.version})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('Route pattern')}
            name="route_pattern"
            rules={[{ required: true, message: t('Enter a route pattern') }]}
            tooltip={t('* matches any characters. Patterns starting with / match paths only; a hostname can also be included: *.example.com/api/*')}
          >
            <Input placeholder={t('E.g. /api/*, *.example.com/*, api.example.com/ws')} />
          </Form.Item>

          <Form.Item
            label={t('Priority')}
            name="priority"
            tooltip={t('Only the highest-priority matching Worker runs per request and phase; on a tie the newer one wins')}
          >
            <InputNumber min={0} max={999} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label={t('Execution phase')} name="phase" tooltip={t('Request phase: runs before contacting the origin; response phase: runs after receiving the response (e.g., rewriting response headers)')}>
            <Select
              options={[
                { value: 'request', label: t('Request phase') },
                { value: 'response', label: t('Response phase') },
              ]}
            />
          </Form.Item>

          <Form.Item label={t('Enable')} name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>

        <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
            <strong>{t('Route pattern example:')}</strong><br />
            • <code>/api/*</code>{t(' - Matches every path under /api/ (including /api/users/123)')}<br />
            • <code>/*.json</code>{t(' - Matches .json files at any path')}<br />
            • <code>api.example.com/*</code>{t(' - Matches only this hostname')}<br />
            • <code>/admin/?/x</code>{t(' - ? matches a single character')}</p>
        </div>
      </Modal>
    </div>
  );
}
